import React, { useState } from 'react';
import { FaQrcode, FaPlus, FaMinus, FaBox, FaTools, FaExclamationTriangle, FaMapMarkerAlt, FaUser, FaMagic, FaHistory, FaUserClock, FaArrowRight, FaHeartbeat, FaWrench, FaCalendarCheck, FaCheck, FaTruck, FaExchangeAlt, FaWarehouse, FaPrint, FaImage, FaBolt } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import styles from '../../pages/Account.module.css';
import ScannerModal from './ScannerModal';
import pb from '../../lib/pocketbase';

const TabInventory = ({ loadingInventory, inventory, fetchInventory, handleUpdateStock, handleAssignTool, user, toast }) => {
    const [showScanner, setShowScanner] = useState(false);
    const [filter, setFilter] = useState('ALL'); // ALL, MATERIEL, OUTIL
    const [selectedItemHistory, setSelectedItemHistory] = useState(null);
    const [itemHistoryData, setItemHistoryData] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [transferItem, setTransferItem] = useState(null);
    const [targetLocation, setTargetLocation] = useState('');
    const [qrItem, setQrItem] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [creationMode, setCreationMode] = useState('quick'); // quick, pro
    const [newItem, setNewItem] = useState({
        name: '',
        type: 'MATERIEL',
        quantity: 0,
        min_quantity: 5,
        location: 'Dépôt Central',
        health: 'excellent',
        image: null,
        imagePreview: null
    });

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setNewItem({
                ...newItem,
                image: file,
                imagePreview: URL.createObjectURL(file)
            });
        }
    };

    const handleCreateItem = async () => {
        if (!newItem.name) return toast.error("Le nom est obligatoire.");
        setLoadingHistory(true);
        try {
            const formData = new FormData();
            formData.append('name', newItem.name);
            formData.append('type', newItem.type);
            formData.append('quantity', newItem.quantity);
            formData.append('min_quantity', newItem.min_quantity);
            formData.append('location', newItem.location);
            formData.append('health', newItem.health);
            if (newItem.image) formData.append('image', newItem.image);

            const record = await pb.collection('inventory').create(formData);

            // Log creation in history
            await logMovement(record.id, "Création article", newItem.quantity, newItem.location, "Saisie initiale");

            toast.success(`"${newItem.name}" a été ajouté à l'inventaire ! ✨`);
            setShowAddModal(false);
            setNewItem({
                name: '',
                type: 'MATERIEL',
                quantity: 0,
                min_quantity: 5,
                location: 'Dépôt Central',
                health: 'excellent',
                image: null,
                imagePreview: null
            });
            fetchInventory();
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la création de l'article.");
        } finally {
            setLoadingHistory(false);
        }
    };

    const locations = ["Dépôt Central", "Camion Jean", "Camion Lucas", "Chantier Bordeaux", "Chantier Arcachon"];

    const logMovement = async (itemId, action, change, location = '', details = '') => {
        try {
            await pb.collection('inventory_history').create({
                item: itemId,
                user: user?.id,
                action: action,
                quantity: change,
                location: location || 'Dépôt Central',
                details: details
            });
        } catch (err) {
            console.error("Erreur log history:", err);
        }
    };

    const fetchHistory = async (item) => {
        setSelectedItemHistory(item);
        setLoadingHistory(true);
        try {
            const records = await pb.collection('inventory_history').getList(1, 15, {
                filter: `item = "${item.id}"`,
                sort: '-created',
                expand: 'user'
            });
            setItemHistoryData(records.items);
        } catch (err) {
            console.error("Fetch history error:", err);
            setItemHistoryData([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleStockAction = async (item, change) => {
        const newQty = item.quantity + change;
        if (newQty < 0) return;

        try {
            await handleUpdateStock(item.id, newQty);
            await logMovement(
                item.id,
                change > 0 ? "Réapprovisionnement" : "Sortie de stock",
                change,
                item.location
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleHealthUpdate = async (item, newHealth) => {
        try {
            await pb.collection('inventory').update(item.id, { health: newHealth });
            toast.success(`État de ${item.name} mis à jour : ${newHealth}`);
            await logMovement(item.id, "Mise à jour état", 0, item.location || 'Dépôt', `Nouvel état : ${newHealth}`);
            fetchInventory();
        } catch (err) {
            toast.error("Erreur lors de la mise à jour de l'état.");
        }
    };

    const handleTransferConfirm = async () => {
        if (!targetLocation || !transferItem) return;
        try {
            await pb.collection('inventory').update(transferItem.id, {
                location: targetLocation
            });
            await logMovement(
                transferItem.id,
                "Transfert",
                0,
                targetLocation,
                `Depuis ${transferItem.location || 'Dépôt'}`
            );
            toast.success(`Transfert de ${transferItem.name} vers ${targetLocation} effectué !`);
            setTransferItem(null);
            fetchInventory();
        } catch (err) {
            toast.error("Erreur lors du transfert.");
        }
    };

    const onScanSuccess = (decodedText, decodedResult) => {
        // Search by ID or Name
        const item = inventory.find(i => i.id === decodedText || i.name.toLowerCase() === decodedText.toLowerCase());

        if (item) {
            toast.success(`Article détecté : ${item.name}`);
            setShowScanner(false);
            // Auto-increment for convenience
            handleUpdateStock(item.id, item.quantity + 1);
        } else {
            toast.error("Article non reconnu. Scannez un QR Code valide.");
        }
    };

    const filteredInventory = inventory.filter(item => {
        if (filter === 'ALL') return true;
        return item.type === filter;
    });

    // Helper to get health style
    const getHealthInfo = (health) => {
        switch (health) {
            case 'excellent': return { label: 'Excellent', class: styles.healthExcellent, icon: <FaHeartbeat /> };
            case 'good': return { label: 'Bon état', class: styles['health-good'], icon: <FaCheck /> };
            case 'worn': return { label: 'Usé', class: styles['health-worn'], icon: <FaExclamationTriangle /> };
            case 'repair': return { label: 'À Réparer', class: styles['health-repair'], icon: <FaWrench /> };
            default: return { label: 'Standard', class: styles['health-good'], icon: <FaCheck /> };
        }
    };

    // Helper to get stock status
    const getStockStatus = (item) => {
        if (item.quantity === 0) return { label: 'Rupture', color: '#ef4444', bg: '#fee2e2' };
        if (item.quantity <= (item.min_quantity || 5)) return { label: 'Critique', color: '#f59e0b', bg: '#fef3c7' };
        return { label: 'En Stock', color: '#10b981', bg: '#d1fae5' };
    };

    return (
        <div className={styles.tabContent}>
            {showScanner && (
                <ScannerModal
                    onClose={() => setShowScanner(false)}
                    onScanSuccess={onScanSuccess}
                />
            )}

            <div className={styles.inventoryHeaderMain}>
                <div className={styles.inventoryTitleGroup}>
                    <h2>
                        <FaBox /> Logistique & Matériel
                    </h2>
                    <p className={styles.inventorySubtitle}>
                        Gestion des stocks et outillage en temps réel
                    </p>
                </div>
                <div className={styles.inventoryActionsGrid}>
                    <button className={styles.btnAddItem} onClick={() => setShowAddModal(true)}>
                        <FaPlus /> Nouvel Article
                    </button>
                    <button className={styles.btnScanner} onClick={() => setShowScanner(true)}>
                        <FaQrcode /> Scanner
                    </button>
                    <button className={styles.btnRefresh} onClick={fetchInventory}>Actualiser</button>
                </div>
            </div>

            {/* FILTERS */}
            <div className={styles.inventoryFiltersModern}>
                <button
                    onClick={() => setFilter('ALL')}
                    className={`${styles.filterBtnModern} ${filter === 'ALL' ? styles.filterBtnModernActive : ''}`}
                >
                    Tout
                </button>
                <button
                    onClick={() => setFilter('MATERIEL')}
                    className={`${styles.filterBtnModern} ${filter === 'MATERIEL' ? styles.filterBtnModernActive : ''}`}
                >
                    <FaBox /> Matériaux
                </button>
                <button
                    onClick={() => setFilter('OUTIL')}
                    className={`${styles.filterBtnModern} ${filter === 'OUTIL' ? styles.filterBtnModernActive : ''}`}
                >
                    <FaTools /> Outillage
                </button>
            </div>

            <div className={styles.card}>
                {loadingInventory ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Chargement...</div>
                ) : filteredInventory.length > 0 ? (
                    <div className={styles.tableResponsive}>
                        <table className={styles.appTable}>
                            <thead>
                                <tr>
                                    <th style={{ width: '60px' }}>Visuel</th>
                                    <th>Article</th>
                                    <th>Type</th>
                                    <th>Localisation</th>
                                    <th>Stock / Assignation</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInventory.map(item => {
                                    const status = getStockStatus(item);
                                    const isTool = item.type === 'OUTIL';
                                    const assignedUser = item.expand?.assigned_to;
                                    const imageUrl = item.image ? pb.files.getURL(item, item.image) : null;

                                    return (
                                        <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td>
                                                <div className={styles.itemImageContainer}>
                                                    {imageUrl ? (
                                                        <img src={imageUrl} alt={item.name} className={styles.itemImage} />
                                                    ) : (
                                                        <div className={styles.itemImagePlaceholder}>
                                                            {isTool ? <FaTools /> : <FaBox />}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.itemIdentity}>
                                                    <div>
                                                        <div style={{ fontWeight: '700', color: '#1e293b' }}>{item.name}</div>
                                                        {isTool && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '5px' }}>
                                                                <div className={`${styles.healthBadge} ${styles['health-' + (item.health || 'good')]}`}>
                                                                    {getHealthInfo(item.health || 'good').icon} {getHealthInfo(item.health || 'good').label}
                                                                </div>
                                                                {item.next_maintenance && (
                                                                    <div className={`${styles.maintenanceAlert} ${new Date(item.next_maintenance) < new Date() ? styles['maintenance-urgent'] : styles['maintenance-ok']}`}>
                                                                        <FaCalendarCheck /> Prox. entretien : {new Date(item.next_maintenance).toLocaleDateString()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {status.label === 'Critique' && !isTool && (
                                                            <div style={{ fontSize: '0.7rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <FaExclamationTriangle /> Seuil bas ({item.min_quantity})
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={styles.typeBadge} style={{
                                                    background: isTool ? '#e0f2fe' : '#f1f5f9',
                                                    color: isTool ? '#0284c7' : '#475569',
                                                    border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600
                                                }}>
                                                    {item.type}
                                                </span>
                                            </td>
                                            <td>
                                                {item.location ? (
                                                    <div className={styles.locationBadge}>
                                                        <FaWarehouse size={12} /> {item.location}
                                                    </div>
                                                ) : <span style={{ color: '#cbd5e1' }}>-</span>}
                                            </td>
                                            <td>
                                                {isTool ? (
                                                    assignedUser ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#3b82f6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>
                                                                {assignedUser.username?.charAt(0).toUpperCase()}
                                                            </div>
                                                            <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{assignedUser.name || assignedUser.username}</span>
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#10b981', background: '#ecfdf5', padding: '2px 8px', borderRadius: '10px', fontSize: '0.8rem' }}>Disponible</span>
                                                    )
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <span style={{ fontSize: '1rem', fontWeight: 'bold', color: item.quantity <= (item.min_quantity || 0) ? '#e11d48' : '#1e293b' }}>
                                                            {item.quantity}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px',
                                                            background: status.bg, color: status.color, fontWeight: 700
                                                        }}>
                                                            {status.label}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {!isTool && (
                                                    <div className={styles.stockActions}>
                                                        <button
                                                            className={styles.stockBtn}
                                                            onClick={() => handleStockAction(item, -1)}
                                                            disabled={item.quantity <= 0}
                                                        >
                                                            <FaMinus />
                                                        </button>
                                                        <button
                                                            className={styles.stockBtn}
                                                            onClick={() => handleStockAction(item, 1)}
                                                        >
                                                            <FaPlus />
                                                        </button>
                                                        <button
                                                            className={styles.miniActionBtn}
                                                            onClick={() => fetchHistory(item)}
                                                            title="Historique des mouvements"
                                                        >
                                                            <FaHistory />
                                                        </button>
                                                        <button
                                                            className={styles.miniActionBtn}
                                                            onClick={() => { setTransferItem(item); setTargetLocation(''); }}
                                                            title="Transférer"
                                                        >
                                                            <FaTruck />
                                                        </button>
                                                        <button
                                                            className={styles.miniActionBtn}
                                                            onClick={() => setQrItem(item)}
                                                            title="Imprimer Étiquette QR"
                                                        >
                                                            <FaQrcode />
                                                        </button>
                                                    </div>
                                                )}
                                                {isTool && (
                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                        <select
                                                            className={styles.healthSelect}
                                                            value={item.health || 'good'}
                                                            onChange={(e) => handleHealthUpdate(item, e.target.value)}
                                                        >
                                                            <option value="excellent">Excellent</option>
                                                            <option value="good">Bon état</option>
                                                            <option value="worn">Usé</option>
                                                            <option value="repair">Réparer</option>
                                                        </select>
                                                        <button className={styles.miniActionBtn} style={{ fontSize: '0.7rem' }}>
                                                            {assignedUser ? 'Récupérer' : 'Assigner'}
                                                        </button>
                                                        <button
                                                            className={styles.miniActionBtn}
                                                            onClick={() => fetchHistory(item)}
                                                            title="Historique de l'outil"
                                                        >
                                                            <FaHistory />
                                                        </button>
                                                        <button
                                                            className={styles.miniActionBtn}
                                                            onClick={() => { setTransferItem(item); setTargetLocation(''); }}
                                                            title="Transférer"
                                                        >
                                                            <FaTruck />
                                                        </button>
                                                        <button
                                                            className={styles.miniActionBtn}
                                                            onClick={() => setQrItem(item)}
                                                            title="Imprimer Étiquette QR"
                                                        >
                                                            <FaQrcode />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles.emptyTable} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        Aucun article trouvé dans cette catégorie.
                    </div>
                )}
            </div>

            {/* QUICK REQUEST */}
            <div className={styles.expressOrderContainer}>
                <h3 className={styles.expressOrderTitle}>
                    <FaMagic style={{ color: 'var(--color-primary)' }} /> Besoin de réappro ? (Express)
                </h3>
                <div className={styles.expressBtnGrid}>
                    {[
                        { name: "Vis Inox 5x60", icon: <FaPlus />, color: "#3b82f6", bg: "rgba(59, 130, 246, 0.1)" },
                        { name: "Nails Pneu 65mm", icon: <FaPlus />, color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" },
                        { name: "Cartouches Sika", icon: <FaPlus />, color: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)" },
                        { name: "Mousse Expansive", icon: <FaPlus />, color: "#ef4444", bg: "rgba(239, 68, 68, 0.1)" }
                    ].map(req => (
                        <button
                            key={req.name}
                            onClick={() => toast.success(`Commande express envoyée : ${req.name}`)}
                            className={styles.expressItemBtn}
                        >
                            <div className={styles.expressIconWrapper} style={{ backgroundColor: req.bg, color: req.color }}>
                                {req.icon}
                            </div>
                            <span className={styles.expressItemName}>{req.name}</span>
                        </button>
                    ))}
                </div>
            </div>
            {/* HISTORY MODAL */}
            {selectedItemHistory && (
                <div className={styles.modalOverlay} onClick={() => setSelectedItemHistory(null)}>
                    <div className={styles.modalCard} onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className={styles.modalHeader}>
                            <h3><FaUserClock style={{ marginRight: '10px' }} /> Historique : {selectedItemHistory.name}</h3>
                            <button className={styles.closeBtn} onClick={() => setSelectedItemHistory(null)}>×</button>
                        </div>
                        <div className={styles.modalBody} style={{ padding: '20px' }}>
                            {loadingHistory ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>Chargement de la traçabilité...</div>
                            ) : (
                                <div className={styles.historyTimeline}>
                                    {itemHistoryData.length > 0 ? itemHistoryData.map(log => (
                                        <div key={log.id} className={styles.timelineItem}>
                                            <div className={styles.timelineIcon}>
                                                {log.change > 0 ? <FaPlus size={12} color="#10b981" /> : <FaMinus size={12} color="#ef4444" />}
                                            </div>
                                            <div className={styles.timelineContent}>
                                                <div className={styles.timelineHeader}>
                                                    <span className={styles.timelineUser}>{log.expand?.user?.name || log.expand?.user?.username || 'Système'}</span>
                                                    <span className={styles.timelineDate}>{new Date(log.created).toLocaleString()}</span>
                                                </div>
                                                <div className={styles.timelineAction}>{log.action}</div>
                                                <div className={styles.timelineChange} style={{
                                                    background: log.change > 0 ? '#d1fae5' : '#fee2e2',
                                                    color: log.change > 0 ? '#10b981' : '#ef4444'
                                                }}>
                                                    {log.change > 0 ? '+' : ''}{log.change} unité(s)
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div style={{ textAlign: 'center', color: '#64748b' }}>Aucun mouvement enregistré.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* TRANSFER MODAL */}
            {transferItem && (
                <div className={styles.modalOverlay} onClick={() => setTransferItem(null)}>
                    <div className={styles.modalCard} onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className={styles.modalHeader}>
                            <h3><FaExchangeAlt style={{ marginRight: '10px' }} /> Transférer : {transferItem.name}</h3>
                            <button className={styles.closeBtn} onClick={() => setTransferItem(null)}>×</button>
                        </div>
                        <div className={styles.modalBody} style={{ padding: '20px' }}>
                            <div className={styles.transferGrid}>
                                <div className={styles.locationCard}>
                                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>Origine</div>
                                    <div style={{ fontWeight: 800 }}>{transferItem.location || 'Dépôt'}</div>
                                </div>
                                <FaExchangeAlt className={styles.transferArrow} />
                                <div className={`${styles.locationCard} ${targetLocation ? styles.active : ''}`}>
                                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>Destination</div>
                                    <div style={{ fontWeight: 800 }}>{targetLocation || '?'}</div>
                                </div>
                            </div>

                            <p style={{ fontSize: '0.9rem', color: '#64748b', textAlign: 'center' }}>Sélectionnez le nouveau lieu d'affectation :</p>

                            <div className={styles.locationSelectGrid}>
                                {locations.filter(l => l !== transferItem.location).map(loc => (
                                    <div
                                        key={loc}
                                        className={`${styles.locOption} ${targetLocation === loc ? styles.selected : ''}`}
                                        onClick={() => setTargetLocation(loc)}
                                    >
                                        {loc}
                                    </div>
                                ))}
                            </div>

                            <button
                                className={styles.submitButton}
                                style={{ width: '100%', marginTop: '25px', justifyContent: 'center' }}
                                disabled={!targetLocation}
                                onClick={handleTransferConfirm}
                            >
                                Confirmer le déplacement
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* QR CODE GENERATOR MODAL */}
            {qrItem && (
                <div className={styles.modalOverlay} onClick={() => setQrItem(null)}>
                    <div className={styles.modalCard} onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        <div className={styles.modalHeader}>
                            <h3><FaQrcode style={{ marginRight: '10px' }} /> Étiquette Inventaire</h3>
                            <button className={styles.closeBtn} onClick={() => setQrItem(null)}>×</button>
                        </div>
                        <div className={styles.modalBody} style={{ padding: '30px', textAlign: 'center' }}>
                            <div className={styles.qrLabelContainer} id="printable-qr">
                                <div className={styles.qrLabelHeader}>MAISON QUINARD</div>
                                <div style={{ background: '#fff', padding: '10px', display: 'inline-block' }}>
                                    <QRCodeSVG
                                        value={qrItem.id}
                                        size={180}
                                        level="H"
                                        includeMargin={false}
                                    />
                                </div>
                                <div className={styles.qrLabelTitle}>{qrItem.name}</div>
                                <div className={styles.qrLabelId}>ID: {qrItem.id.substring(0, 8)}</div>
                                <div style={{ fontSize: '0.65rem', opacity: 0.6 }}>LOGISTIQUE CONNECTÉE . MQ v1.0</div>
                            </div>

                            <button
                                className={styles.qrPrintBtn}
                                onClick={() => {
                                    window.print();
                                    toast.success("Impravion lancée !");
                                }}
                            >
                                <FaPrint /> Imprimer l'étiquette
                            </button>
                            <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '15px' }}>
                                Conseil : Imprimez sur du papier adhésif résistant pour une durabilité maximale sur le terrain.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* ADD ITEM MODAL */}
            {showAddModal && (
                <div className={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
                    <div className={styles.modalCard} onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className={styles.modalHeader}>
                            <h3><FaPlus style={{ marginRight: '10px' }} /> Ajouter au Stock</h3>
                            <button className={styles.closeBtn} onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <div className={styles.modalBody} style={{ padding: '25px' }}>
                            <div className={styles.creationTabGroup}>
                                <div
                                    className={`${styles.creationTab} ${creationMode === 'quick' ? styles.creationTabActive : ''}`}
                                    onClick={() => setCreationMode('quick')}
                                >
                                    <FaBolt /> Mode Rapide
                                </div>
                                <div
                                    className={`${styles.creationTab} ${creationMode === 'pro' ? styles.creationTabActive : ''}`}
                                    onClick={() => setCreationMode('pro')}
                                >
                                    <FaTools /> Mode Complet (Pro)
                                </div>
                            </div>

                            <div className={styles.inventoryForm}>
                                <div className={styles.formGridTwo}>
                                    <div style={{ gridColumn: creationMode === 'pro' ? 'span 2' : 'span 2' }}>
                                        <label className={styles.formLabel}>Nom de l'article</label>
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="Ex: Vis Inox 5x60, Perceuse Hilti..."
                                            value={newItem.name}
                                            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className={styles.formLabel}>Type</label>
                                        <select
                                            className={styles.formInput}
                                            value={newItem.type}
                                            onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                                        >
                                            <option value="MATERIEL">📦 Matériau / Consommable</option>
                                            <option value="OUTIL">🛠️ Outillage / Machine</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className={styles.formLabel}>Quantité Initiale</label>
                                        <input
                                            type="number"
                                            className={styles.formInput}
                                            value={newItem.quantity}
                                            onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) })}
                                        />
                                    </div>

                                    {creationMode === 'pro' && (
                                        <>
                                            <div>
                                                <label className={styles.formLabel}>Seuil d'alerte (Min)</label>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <input
                                                        type="number"
                                                        className={styles.formInput}
                                                        value={newItem.min_quantity}
                                                        onChange={(e) => setNewItem({ ...newItem, min_quantity: parseInt(e.target.value) })}
                                                    />
                                                    <button
                                                        className={styles.btnRefresh}
                                                        style={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                                        onClick={() => setNewItem({ ...newItem, min_quantity: Math.ceil(newItem.quantity * 0.2) })}
                                                    >
                                                        Auto (20%)
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className={styles.formLabel}>Localisation</label>
                                                <select
                                                    className={styles.formInput}
                                                    value={newItem.location}
                                                    onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                                                >
                                                    {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                                                </select>
                                            </div>

                                            <div style={{ gridColumn: 'span 2' }}>
                                                <label className={styles.formLabel}>Photo de l'article</label>
                                                <input
                                                    type="file"
                                                    id="item-image-upload"
                                                    hidden
                                                    onChange={handleFileChange}
                                                    accept="image/*"
                                                />
                                                <div
                                                    className={styles.imageUploadZone}
                                                    onClick={() => document.getElementById('item-image-upload').click()}
                                                >
                                                    {newItem.imagePreview ? (
                                                        <div>
                                                            <img src={newItem.imagePreview} alt="Preview" className={styles.imagePreview} />
                                                            <p style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 'bold' }}>Changer la photo</p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <FaImage size={30} style={{ color: '#cbd5e1', marginBottom: '10px' }} />
                                                            <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                                Cliquez pour prendre une photo ou uploader
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <button
                                    className={styles.submitButton}
                                    style={{ width: '100%', marginTop: '30px', height: '50px', justifyContent: 'center', fontSize: '1rem' }}
                                    disabled={loadingHistory}
                                    onClick={handleCreateItem}
                                >
                                    {loadingHistory ? "Création en cours..." : "Valider et Créer l'article"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabInventory;
